import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, ArrowDown, Plus, Upload, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Lines, SectionTag } from "../components/site/Motion";
import { api, formatApiError } from "../lib/api";

const COPY = {
  en: {
    seo: {
      title: "SWAPA Hospitality | Electric Mobility for Hotels & Resorts",
      desc: "Discover SWAPA Hospitality: compact electric mobility for hotels, resorts and exceptional destinations, customizable with your property's brand identity.",
      path: "/hospitality",
    },
    hero: {
      eyebrow: "SWAPA Hospitality",
      lines: ["Your hospitality.", "In motion."],
      copy: "Compact electric mobility designed for hotels, resorts and exceptional destinations. Move guests, support your team and turn every journey into part of your brand experience.",
      cta1: "Design your SWAPA",
      cta2: "Discover hospitality uses",
      micro: "Send us your logo. We'll show you how your SWAPA could look.",
      img: "/assets/hospitality/hero-palazzo-manzoni.webp",
      imgMobile: "/assets/hospitality/hero-palazzo-manzoni-mobile.webp",
      alt: "SWAPA with Palazzo Manzoni hotel livery — branded electric vehicle for luxury hotels",
    },
    intro: {
      tag: "Brand Experience",
      lines: ["Every journey is", "part of the experience."],
      journeys: ["From reception to suite.", "From villa to beach club.", "From parking to restaurant."],
      copy: "Hospitality is made of hundreds of small journeys every day. SWAPA transforms these movements into a distinctive experience for guests, staff and the property itself.",
      punch: ["It is not simply a vehicle.", "It becomes part of the destination."],
      img: "/assets/hospitality/intro-aurora-beach.webp",
      alt: "Guest with SWAPA electric vehicle at Aurora Beach Resort at sunset — electric mobility for beach resorts",
    },
    pillarsTag: "Three Roles, One Vehicle",
    pillars: [
      {
        index: "01", tag: "Guest Experience", title: "Move your guests differently.",
        copy: "Create a distinctive mobility experience for short transfers across resorts, hospitality destinations and connected areas.",
        apps: ["Guest transfers", "Reception to room", "Parking to hotel", "Villa transfers", "Restaurant connections", "Spa transfers", "Beach club connections"],
        img: "/assets/hospitality/pillar-guest-experience.webp",
        alt: "Valet handing SWAPA keys to a guest with shopping bags at the Grand Hotel entrance — hotel electric vehicle for guest transfers",
      },
      {
        index: "02", tag: "Operations", title: "Make hospitality move.",
        copy: "A compact vehicle for everyday mobility across the property, helping teams move efficiently between different areas and services.",
        apps: ["Concierge", "Management", "Guest relations", "Technical staff", "Light service operations", "Internal property mobility"],
        img: "/assets/hospitality/pillar-operations.webp",
        alt: "SWAPA with Hotel Concierge branding at the staff entrance of Tulip Hotel Forte dei Marmi — hotel service vehicle",
      },
      {
        index: "03", tag: "Brand", title: "Your brand doesn't stop at the lobby.",
        copy: "Transform SWAPA into a recognizable extension of your hospitality identity. Customize the vehicle with your logo, colours and visual language to create a mobility touchpoint that belongs to your destination.",
        apps: [],
        img: "/assets/hospitality/pillar-brand.webp",
        alt: "SWAPA Hotel Pitti courtesy car in Via Condotti Rome — branded electric vehicle for hotels visible in the city",
      },
    ],
    custom: {
      tag: "Brand Customization",
      lines: ["Designed around your property.", "Branded around your identity."],
      livery: "Concept livery — fictional identity",
      mockups: [
        { img: "/assets/hospitality/mockup-marea-club.webp", mark: null, kind: "Mediterranean Beach Resort", alt: "SWAPA with Marea Club Beach Resort door livery — branded electric vehicle for beach resorts" },
        { img: "/assets/hospitality/mockup-tenuta-vigneto.webp", mark: null, kind: "Tuscan Wine Resort", alt: "SWAPA with Tenuta Vigneto Wine Resort Toscana door livery — branded electric vehicle for wine resorts" },
        { img: "/assets/hospitality/mockup-alpenhof.webp", mark: null, kind: "Alpine Luxury Hotel", alt: "SWAPA with Alpenhof 1873 door livery — branded electric vehicle for alpine luxury hotels" },
        { img: "/assets/hospitality/mockup-palazzo-nove.webp", mark: null, kind: "Contemporary Urban Hotel", alt: "SWAPA with Palazzo Nove door livery — branded electric vehicle for contemporary urban hotels" },
      ],
      options: ["Property logo", "Bespoke exterior graphics", "Brand colours", "Door branding", "Hotel or resort naming", "Fleet numbering", "Special campaign graphics"],
      cta: "Create your SWAPA",
      micro: "Send us your logo and brand identity. Our team will prepare a personalized SWAPA concept for your property.",
    },
    comparison: {
      tag: "The Difference",
      lines: ["Beyond the traditional", "resort vehicle."],
      copy: "SWAPA is not an open hospitality cart. It is an enclosed, automotive-grade electric vehicle with a compact footprint — designed to carry your guests, your team and your identity with the same care you put into every detail of your property.",
      legal: "SWAPA is an L6e-homologated quadricycle. Public-road use is subject to local laws, registration and applicable regulations — our team will help you evaluate what applies to your destination.",
      link: "Explore the technology",
      items: ["Enclosed cabin", "Protection from weather", "Automotive design", "Compact footprint", "Electric propulsion", "Two-seat configuration", "Brand customization", "Designed for both hospitality experience and operational mobility"],
    },
    form: {
      tag: "Design Your SWAPA",
      lines: ["Send us your logo.", "We'll design your SWAPA."],
      copy: "Tell us about your property and send us your logo. Our team will create a preliminary visual concept showing how SWAPA could become part of your hospitality experience.",
      bullets: ["Preliminary branded concept for your property", "Fleet and use-case consultation", "No commitment required"],
      labels: {
        first_name: "First name", last_name: "Last name", company: "Company / Property name", role: "Role",
        email: "Email", phone: "Phone", website: "Website", property_type: "Property type", city: "City", country: "Country",
        fleet: "How many vehicles could your property require?", uses: "Primary intended use",
        logo: "Upload your logo", guidelines: "Upload your brand guidelines", message: "Tell us about your property",
      },
      optional: "(optional)",
      fileHint: "JPG, PNG, WEBP, SVG or PDF — max 10 MB",
      select: "Select…",
      propertyTypes: ["Hotel", "Luxury Hotel", "Resort", "Boutique Hotel", "Golf Resort", "Wine Resort", "Beach Club", "Marina", "Glamping / Village", "Branded Residence", "Villa / Property Management", "Hospitality Group", "Other"],
      usesOptions: ["Guest mobility", "Concierge", "Operations", "Management", "Events", "Brand activation", "Other"],
      messagePlaceholder: "Location, guests, distances, experiences you'd like to create…",
      privacyPre: "I have read the",
      privacyLink: "privacy policy",
      privacyPost: "and I consent to the processing of my data to be contacted about SWAPA Hospitality. *",
      submit: "Design my SWAPA",
      sending: "Sending…",
      successTitle: ["Your SWAPA concept", "is on its way."],
      successCopy: (name, company, hasLogo) =>
        `Thank you${name ? `, ${name}` : ""}. Our hospitality team has received your brief${hasLogo ? " and your logo" : ""} and will prepare a preliminary visual concept for ${company || "your property"}. We'll be in touch shortly.`,
      successFoot: "SWAPA Hospitality — Your hospitality. In motion.",
    },
    final: {
      lines: ["Make mobility", "part of your hospitality."],
      tagline: "SWAPA Hospitality — Your hospitality. In motion.",
      cta1: "Design your SWAPA",
      cta2: "Contact our hospitality team",
      testdrivePre: "Prefer to experience SWAPA first?",
      testdriveLink: "Book a test drive",
    },
    sticky: "Design your SWAPA",
  },
  it: {
    seo: {
      title: "SWAPA Hospitality | Veicolo elettrico per hotel e resort",
      desc: "SWAPA Hospitality: mobilità elettrica compatta per hotel, resort e destinazioni d'eccezione. Microcar elettrica personalizzabile con l'identità della tua struttura.",
      path: "/hospitality/it",
    },
    hero: {
      eyebrow: "SWAPA Hospitality",
      lines: ["La tua ospitalità.", "In movimento."],
      copy: "Mobilità elettrica compatta pensata per hotel, resort e destinazioni d'eccezione. Muovi gli ospiti, supporta il tuo team e trasforma ogni spostamento in parte dell'esperienza del tuo brand.",
      cta1: "Disegna la tua SWAPA",
      cta2: "Scopri gli usi in hospitality",
      micro: "Inviaci il tuo logo. Ti mostreremo come potrebbe essere la tua SWAPA.",
      img: "/assets/hospitality/hero-palazzo-manzoni.webp",
      imgMobile: "/assets/hospitality/hero-palazzo-manzoni-mobile.webp",
      alt: "SWAPA con livrea Palazzo Manzoni — veicolo elettrico brandizzato per hotel di lusso",
    },
    intro: {
      tag: "Esperienza di brand",
      lines: ["Ogni spostamento è", "parte dell'esperienza."],
      journeys: ["Dalla reception alla suite.", "Dalla villa al beach club.", "Dal parcheggio al ristorante."],
      copy: "L'hospitality è fatta di centinaia di piccoli spostamenti ogni giorno. SWAPA trasforma questi movimenti in un'esperienza distintiva per gli ospiti, lo staff e la struttura stessa.",
      punch: ["Non è semplicemente un veicolo.", "Diventa parte della destinazione."],
      img: "/assets/hospitality/intro-aurora-beach.webp",
      alt: "Ospite con veicolo elettrico SWAPA all'Aurora Beach Resort al tramonto — mobilità elettrica per beach resort",
    },
    pillarsTag: "Tre ruoli, un veicolo",
    pillars: [
      {
        index: "01", tag: "Esperienza ospite", title: "Muovi i tuoi ospiti in modo diverso.",
        copy: "Crea un'esperienza di mobilità distintiva per i brevi trasferimenti all'interno di resort, destinazioni hospitality e aree connesse.",
        apps: ["Trasferimenti ospiti", "Reception → camera", "Parcheggio → hotel", "Transfer ville", "Collegamenti ristorante", "Transfer spa", "Collegamenti beach club"],
        img: "/assets/hospitality/pillar-guest-experience.webp",
        alt: "Valet consegna la chiave della SWAPA a un'ospite con shopping bag davanti al Grand Hotel — veicolo elettrico per hotel e trasferimenti ospiti",
      },
      {
        index: "02", tag: "Operazioni", title: "Fai muovere l'hospitality.",
        copy: "Un veicolo di servizio compatto per la mobilità quotidiana nella struttura, che aiuta i team a spostarsi in modo efficiente tra aree e servizi.",
        apps: ["Concierge", "Management", "Relazioni con gli ospiti", "Staff tecnico", "Operazioni di servizio leggere", "Mobilità interna alla struttura"],
        img: "/assets/hospitality/pillar-operations.webp",
        alt: "SWAPA con branding Hotel Concierge all'ingresso staff del Tulip Hotel Forte dei Marmi — veicolo di servizio per hotel",
      },
      {
        index: "03", tag: "Brand", title: "Il tuo brand non si ferma alla lobby.",
        copy: "Trasforma SWAPA in un'estensione riconoscibile della tua identità hospitality. Personalizza il veicolo con logo, colori e linguaggio visivo per creare un touchpoint di mobilità che appartiene alla tua destinazione.",
        apps: [],
        img: "/assets/hospitality/pillar-brand.webp",
        alt: "SWAPA courtesy car Hotel Pitti in Via Condotti a Roma — veicolo elettrico brandizzato per hotel visibile in città",
      },
    ],
    custom: {
      tag: "Personalizzazione del brand",
      lines: ["Progettata intorno alla tua struttura.", "Brandizzata intorno alla tua identità."],
      livery: "Livrea concept — identità fittizia",
      mockups: [
        { img: "/assets/hospitality/mockup-marea-club.webp", mark: null, kind: "Beach resort mediterraneo", alt: "SWAPA con livrea Marea Club Beach Resort sulla portiera — veicolo elettrico brandizzato per beach resort" },
        { img: "/assets/hospitality/mockup-tenuta-vigneto.webp", mark: null, kind: "Wine resort toscano", alt: "SWAPA con livrea Tenuta Vigneto Wine Resort Toscana sulla portiera — veicolo elettrico brandizzato per wine resort" },
        { img: "/assets/hospitality/mockup-alpenhof.webp", mark: null, kind: "Hotel di lusso alpino", alt: "SWAPA con livrea Alpenhof 1873 sulla portiera — veicolo elettrico brandizzato per hotel alpini di lusso" },
        { img: "/assets/hospitality/mockup-palazzo-nove.webp", mark: null, kind: "Urban hotel contemporaneo", alt: "SWAPA con livrea Palazzo Nove sulla portiera — veicolo elettrico brandizzato per urban hotel contemporanei" },
      ],
      options: ["Logo della struttura", "Grafiche esterne su misura", "Colori del brand", "Branding sulle porte", "Nome dell'hotel o resort", "Numerazione flotta", "Grafiche per campagne speciali"],
      cta: "Crea la tua SWAPA",
      micro: "Inviaci logo e identità del brand. Il nostro team preparerà un concept SWAPA personalizzato per la tua struttura.",
    },
    comparison: {
      tag: "La differenza",
      lines: ["Oltre il tradizionale", "veicolo da resort."],
      copy: "SWAPA non è un cart hospitality aperto. È un veicolo elettrico chiuso, di stampo automobilistico e dall'ingombro compatto — progettato per trasportare ospiti, team e identità con la stessa cura che dedichi a ogni dettaglio della tua struttura.",
      legal: "SWAPA è un quadriciclo omologato L6e. L'uso su strada pubblica è soggetto alle leggi locali, all'immatricolazione e alle normative applicabili — il nostro team ti aiuterà a valutare cosa si applica alla tua destinazione.",
      link: "Scopri la tecnologia",
      items: ["Abitacolo chiuso", "Protezione dalle intemperie", "Design automotive", "Ingombro compatto", "Propulsione elettrica", "Configurazione a due posti", "Personalizzazione del brand", "Pensata per l'esperienza degli ospiti e la mobilità operativa"],
    },
    form: {
      tag: "Disegna la tua SWAPA",
      lines: ["Inviaci il tuo logo.", "Disegneremo la tua SWAPA."],
      copy: "Raccontaci la tua struttura e inviaci il tuo logo. Il nostro team creerà un concept visivo preliminare che mostra come SWAPA potrebbe diventare parte della tua esperienza di hospitality.",
      bullets: ["Concept brandizzato preliminare per la tua struttura", "Consulenza su flotta e casi d'uso", "Nessun impegno richiesto"],
      labels: {
        first_name: "Nome", last_name: "Cognome", company: "Azienda / Nome della struttura", role: "Ruolo",
        email: "Email", phone: "Telefono", website: "Sito web", property_type: "Tipo di struttura", city: "Città", country: "Paese",
        fleet: "Di quanti veicoli potrebbe avere bisogno la tua struttura?", uses: "Uso principale previsto",
        logo: "Carica il tuo logo", guidelines: "Carica le tue brand guidelines", message: "Raccontaci la tua struttura",
      },
      optional: "(opzionale)",
      fileHint: "JPG, PNG, WEBP, SVG o PDF — max 10 MB",
      select: "Seleziona…",
      propertyTypes: ["Hotel", "Hotel di lusso", "Resort", "Boutique hotel", "Golf resort", "Wine resort", "Beach club", "Marina", "Glamping / Villaggio", "Branded residence", "Villa / Gestione proprietà", "Gruppo alberghiero", "Altro"],
      usesOptions: ["Mobilità ospiti", "Concierge", "Operazioni", "Management", "Eventi", "Attivazione brand", "Altro"],
      messagePlaceholder: "Località, ospiti, distanze, esperienze che vorresti creare…",
      privacyPre: "Ho letto l'",
      privacyLink: "informativa privacy",
      privacyPost: "e acconsento al trattamento dei miei dati per essere contattato riguardo a SWAPA Hospitality. *",
      submit: "Disegna la mia SWAPA",
      sending: "Invio…",
      successTitle: ["Il concept della tua SWAPA", "è in arrivo."],
      successCopy: (name, company, hasLogo) =>
        `Grazie${name ? `, ${name}` : ""}. Il nostro team hospitality ha ricevuto il tuo brief${hasLogo ? " e il tuo logo" : ""} e preparerà un concept visivo preliminare per ${company || "la tua struttura"}. Ti ricontatteremo a breve.`,
      successFoot: "SWAPA Hospitality — La tua ospitalità. In movimento.",
    },
    final: {
      lines: ["Rendi la mobilità", "parte della tua ospitalità."],
      tagline: "SWAPA Hospitality — La tua ospitalità. In movimento.",
      cta1: "Disegna la tua SWAPA",
      cta2: "Contatta il team hospitality",
      testdrivePre: "Preferisci provare prima SWAPA?",
      testdriveLink: "Prenota un test drive",
    },
    sticky: "Disegna la tua SWAPA",
  },
};

const FLEET_SIZES = ["1", "2–3", "4–5", "6–10", "10+"];

function scrollToForm() {
  document.getElementById("design-your-swapa")?.scrollIntoView({ behavior: "smooth" });
}

function Hero({ c }) {
  const reduce = useReducedMotion();
  return (
    <section data-testid="hospitality-hero" className="relative flex min-h-[100svh] items-end overflow-hidden">
      <div className="absolute inset-0" aria-hidden="true">
        <motion.img
          src={c.hero.img}
          srcSet={`${c.hero.imgMobile} 1024w, ${c.hero.img} 1536w`}
          sizes="100vw"
          alt={c.hero.alt}
          fetchpriority="high"
          decoding="async"
          className="h-full w-full object-cover"
          initial={reduce ? false : { scale: 1.08 }}
          animate={reduce ? {} : { scale: 1 }}
          transition={{ duration: 6, ease: "easeOut" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/45 to-ink/30" />
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/60 to-transparent" />
      </div>
      <div className="relative mx-auto w-full max-w-[1600px] px-5 pb-24 pt-40 sm:px-10 sm:pb-32">
        <Reveal y={20}>
          <p data-testid="hospitality-eyebrow" className="font-mono text-[10px] uppercase tracking-[0.35em] text-swapa-yellow">{c.hero.eyebrow}</p>
        </Reveal>
        <h1 className="mt-6 font-display font-extrabold uppercase leading-[0.9] tracking-tight">
          <Lines animate="load" lines={c.hero.lines} stagger={0.16} delay={0.15} className="text-5xl text-white sm:text-7xl lg:text-8xl" />
        </h1>
        <Reveal delay={0.5}>
          <p className="mt-8 max-w-xl font-serif text-lg italic leading-relaxed text-zinc-300 sm:text-xl">{c.hero.copy}</p>
        </Reveal>
        <Reveal delay={0.65}>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button onClick={scrollToForm} data-testid="hero-cta-design"
              className="group inline-flex items-center gap-3 bg-swapa-yellow px-8 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-white">
              {c.hero.cta1}
              <ArrowDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <a href="#pillars" data-testid="hero-cta-discover"
              className="group inline-flex items-center gap-3 border border-white/20 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:border-swapa-yellow hover:text-swapa-yellow">
              {c.hero.cta2}
            </a>
          </div>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{c.hero.micro}</p>
        </Reveal>
      </div>
    </section>
  );
}

function Intro({ c }) {
  return (
    <section className="border-t border-white/10 py-28 sm:py-40">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-10">
        <SectionTag index="01" label={c.intro.tag} />
        <h2 className="mt-12 font-display font-extrabold uppercase leading-[0.95] tracking-tight">
          <Lines lines={c.intro.lines} stagger={0.14} className="text-4xl text-white sm:text-6xl lg:text-7xl" />
        </h2>
        <div className="mt-16 grid gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            {c.intro.journeys.map((j, i) => (
              <Reveal key={j} delay={i * 0.1}>
                <p className="border-t border-white/10 py-5 font-display text-2xl font-bold uppercase tracking-tight text-white/85 sm:text-3xl">{j}</p>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <p className="mt-8 max-w-md font-serif text-lg italic leading-relaxed text-zinc-400">{c.intro.copy}</p>
              <p className="mt-6 font-display text-xl font-bold uppercase tracking-tight text-swapa-yellow sm:text-2xl">
                {c.intro.punch[0]}<br />{c.intro.punch[1]}
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.2} y={60}>
            <figure className="overflow-hidden border border-white/10">
              <img src={c.intro.img} alt={c.intro.alt} className="aspect-[4/5] w-full object-cover" loading="lazy" decoding="async" />
            </figure>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Pillars({ c }) {
  return (
    <section id="pillars" className="border-t border-white/10 py-28 sm:py-40">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-10">
        <SectionTag index="02" label={c.pillarsTag} />
        <div className="mt-14 grid gap-px border border-white/10 bg-white/10 lg:grid-cols-3" data-testid="pillars-grid">
          {c.pillars.map((p, i) => (
            <Reveal key={p.tag} delay={i * 0.12} className="bg-ink">
              <article className="group flex h-full flex-col p-8 sm:p-10">
                <div className="overflow-hidden border border-white/10">
                  <img src={p.img} alt={p.alt} loading="lazy" decoding="async"
                    className="aspect-[16/10] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
                </div>
                <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.3em] text-swapa-yellow">{p.index} — {p.tag}</p>
                <h3 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">{p.title}</h3>
                <p className="mt-4 font-serif text-base italic leading-relaxed text-zinc-400">{p.copy}</p>
                {p.apps.length > 0 && (
                  <ul className="mt-6 flex flex-wrap gap-2">
                    {p.apps.map((a) => (
                      <li key={a} className="border border-white/10 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-400">{a}</li>
                    ))}
                  </ul>
                )}
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Customization({ c }) {
  return (
    <section className="border-t border-white/10 py-28 sm:py-40">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-10">
        <SectionTag index="03" label={c.custom.tag} />
        <h2 className="mt-12 font-display font-extrabold uppercase leading-[0.95] tracking-tight">
          <Lines lines={c.custom.lines} stagger={0.14} className="text-4xl text-white sm:text-6xl lg:text-6xl" />
        </h2>
        <div className="mt-16 grid grid-cols-2 gap-5 sm:gap-8 lg:grid-cols-4" data-testid="customization-grid">
          {c.custom.mockups.map((m, i) => (
            <Reveal key={m.kind} delay={i * 0.08}>
              <figure className={`group relative overflow-hidden border border-white/10 ${i % 2 === 1 ? "lg:mt-14" : ""}`}>
                <img src={m.img} alt={m.alt} loading="lazy" decoding="async"
                  className="aspect-[3/4] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" aria-hidden="true" />
                {m.mark && (
                  <figcaption className="absolute inset-x-0 top-0 p-4 text-center">
                    <span className="font-display text-sm font-extrabold uppercase tracking-[0.2em] text-white drop-shadow sm:text-base">{m.mark}</span>
                  </figcaption>
                )}
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-300">{m.kind}</p>
                  <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.22em] text-swapa-yellow">{c.custom.livery}</p>
                </div>
              </figure>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <ul className="mt-14 flex flex-wrap gap-2.5" data-testid="customization-options">
            {c.custom.options.map((opt) => (
              <li key={opt} className="flex items-center gap-2 border border-white/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-300">
                <Plus className="h-3 w-3 text-swapa-yellow" />{opt}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-12 flex flex-wrap items-center gap-6">
            <button onClick={scrollToForm} data-testid="customization-cta"
              className="group inline-flex items-center gap-3 bg-swapa-yellow px-8 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-white">
              {c.custom.cta}
              <ArrowDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <p className="max-w-sm font-mono text-[10px] uppercase leading-relaxed tracking-[0.18em] text-zinc-500">{c.custom.micro}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Comparison({ c }) {
  return (
    <section className="border-t border-white/10 py-28 sm:py-40">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-10">
        <SectionTag index="04" label={c.comparison.tag} />
        <div className="mt-12 grid gap-14 lg:grid-cols-2 lg:gap-24">
          <div>
            <h2 className="font-display font-extrabold uppercase leading-[0.95] tracking-tight">
              <Lines lines={c.comparison.lines} stagger={0.14} className="text-4xl text-white sm:text-5xl lg:text-6xl" />
            </h2>
            <Reveal delay={0.25}>
              <p className="mt-8 max-w-md font-serif text-lg italic leading-relaxed text-zinc-400">{c.comparison.copy}</p>
              <p className="mt-6 text-sm leading-relaxed text-zinc-500">
                {c.comparison.legal}{" "}
                <Link to="/tecnologia" className="text-swapa-yellow underline-offset-4 transition-colors hover:text-white hover:underline" data-testid="comparison-tech-link">
                  {c.comparison.link}
                </Link>
                .
              </p>
            </Reveal>
          </div>
          <ul className="grid content-start gap-px border border-white/10 bg-white/10 sm:grid-cols-2" data-testid="comparison-list">
            {c.comparison.items.map((item, i) => (
              <li key={item} className="bg-ink">
                <Reveal delay={i * 0.06} y={24}>
                  <p className="flex h-full items-center gap-3 p-5 font-mono text-[10px] uppercase leading-relaxed tracking-[0.18em] text-zinc-200">
                    <Plus className="h-3.5 w-3.5 shrink-0 text-swapa-yellow" />{item}
                  </p>
                </Reveal>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

const inputCls = "w-full border border-white/15 bg-black/40 px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 focus:border-swapa-yellow focus:outline-none transition-colors";

function FormField({ label, id, required = false, ...props }) {
  return (
    <label htmlFor={id} className="block">
      <span className="mb-2 block font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
        {label}{required && <span className="text-swapa-yellow"> *</span>}
      </span>
      <input id={id} data-testid={id} required={required} {...props} className={inputCls} />
    </label>
  );
}

function FilePicker({ label, id, file, onChange, hint, optionalLabel }) {
  return (
    <div>
      <span className="mb-2 block font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
        {label} <span className="normal-case text-zinc-600">{optionalLabel}</span>
      </span>
      <label htmlFor={id} data-testid={`${id}-drop`}
        className="flex cursor-pointer items-center gap-3 border border-dashed border-white/20 bg-black/40 px-4 py-3.5 transition-colors hover:border-swapa-yellow">
        {file ? <Check className="h-4 w-4 shrink-0 text-swapa-yellow" /> : <Upload className="h-4 w-4 shrink-0 text-zinc-500" />}
        <span className={`truncate text-sm ${file ? "text-white" : "text-zinc-500"}`}>{file ? file.name : hint}</span>
        {file && (
          <button type="button" aria-label={`Remove ${file.name}`} data-testid={`${id}-remove`}
            onClick={(e) => { e.preventDefault(); onChange(null); }}
            className="ml-auto text-zinc-500 transition-colors hover:text-white">
            <X className="h-4 w-4" />
          </button>
        )}
      </label>
      <input id={id} data-testid={id} type="file" accept=".jpg,.jpeg,.png,.webp,.gif,.svg,.pdf" className="sr-only"
        onChange={(e) => onChange(e.target.files?.[0] || null)} />
    </div>
  );
}

function LeadForm({ c, lang }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", company: "", role: "", email: "", phone: "",
    website: "", property_type: "", city: "", country: "", fleet_size: "", message: "",
  });
  const [uses, setUses] = useState([]);
  const [logo, setLogo] = useState(null);
  const [guidelines, setGuidelines] = useState(null);
  const [privacy, setPrivacy] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const utm = useRef({ utm_source: "", utm_medium: "", utm_campaign: "" });
  const L = c.form.labels;

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    utm.current = {
      utm_source: q.get("utm_source") || "",
      utm_medium: q.get("utm_medium") || "",
      utm_campaign: q.get("utm_campaign") || "",
    };
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleUse = (u) => setUses((prev) => (prev.includes(u) ? prev.filter((x) => x !== u) : [...prev, u]));

  async function submit(e) {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries({ ...form, ...utm.current }).forEach(([k, v]) => fd.append(k, v));
      uses.forEach((u) => fd.append("intended_uses", u));
      fd.append("privacy", privacy ? "true" : "false");
      fd.append("lead_source", "SWAPA_HOSPITALITY");
      fd.append("lang", lang);
      if (logo) fd.append("logo", logo);
      if (guidelines) fd.append("guidelines", guidelines);
      await api.post("/leads/hospitality", fd);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "hospitality_lead", property_type: form.property_type, fleet_size: form.fleet_size, lang });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  }

  if (done) {
    return (
      <div data-testid="hospitality-form-success" className="border border-swapa-yellow/40 bg-black/40 p-10 text-center sm:p-16">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-swapa-yellow">
          <Check className="h-6 w-6 text-swapa-yellow" />
        </span>
        <h3 className="mt-8 font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">
          {c.form.successTitle[0]}<br />{c.form.successTitle[1]}
        </h3>
        <p className="mx-auto mt-6 max-w-md font-serif text-lg italic leading-relaxed text-zinc-400">
          {c.form.successCopy(form.first_name, form.company, !!logo)}
        </p>
        <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{c.form.successFoot}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="hospitality-form" className="border border-white/10 bg-black/40 p-6 sm:p-12">
      <div className="grid gap-6 sm:grid-cols-2">
        <FormField label={L.first_name} id="hf-first-name" required value={form.first_name} onChange={set("first_name")} autoComplete="given-name" />
        <FormField label={L.last_name} id="hf-last-name" required value={form.last_name} onChange={set("last_name")} autoComplete="family-name" />
        <FormField label={L.company} id="hf-company" required value={form.company} onChange={set("company")} autoComplete="organization" />
        <FormField label={L.role} id="hf-role" value={form.role} onChange={set("role")} autoComplete="organization-title" />
        <FormField label={L.email} id="hf-email" required type="email" value={form.email} onChange={set("email")} autoComplete="email" />
        <FormField label={L.phone} id="hf-phone" type="tel" value={form.phone} onChange={set("phone")} autoComplete="tel" />
        <FormField label={L.website} id="hf-website" type="url" placeholder="https://" value={form.website} onChange={set("website")} autoComplete="url" />
        <label htmlFor="hf-property-type" className="block">
          <span className="mb-2 block font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">{L.property_type}</span>
          <select id="hf-property-type" data-testid="hf-property-type" value={form.property_type} onChange={set("property_type")} className={inputCls}>
            <option value="">{c.form.select}</option>
            {c.form.propertyTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <FormField label={L.city} id="hf-city" value={form.city} onChange={set("city")} autoComplete="address-level2" />
        <FormField label={L.country} id="hf-country" value={form.country} onChange={set("country")} autoComplete="country-name" />
      </div>

      <div className="mt-8">
        <span className="mb-2 block font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">{L.fleet}</span>
        <div className="flex flex-wrap gap-2" data-testid="hf-fleet-size">
          {FLEET_SIZES.map((s) => (
            <button type="button" key={s} data-testid={`hf-fleet-${s}`} onClick={() => setForm((f) => ({ ...f, fleet_size: s }))}
              className={`border px-5 py-2.5 font-mono text-[11px] tracking-[0.15em] transition-colors duration-300 ${form.fleet_size === s ? "border-swapa-yellow bg-swapa-yellow text-black" : "border-white/15 text-zinc-300 hover:border-swapa-yellow hover:text-swapa-yellow"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8">
        <span className="mb-2 block font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">{L.uses}</span>
        <div className="flex flex-wrap gap-2" data-testid="hf-intended-uses">
          {c.form.usesOptions.map((u) => (
            <button type="button" key={u} data-testid={`hf-use-${u.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => toggleUse(u)} aria-pressed={uses.includes(u)}
              className={`border px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 ${uses.includes(u) ? "border-swapa-yellow bg-swapa-yellow/10 text-swapa-yellow" : "border-white/15 text-zinc-300 hover:border-swapa-yellow hover:text-swapa-yellow"}`}>
              {u}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 grid gap-6 sm:grid-cols-2">
        <FilePicker label={L.logo} id="hf-logo" file={logo} onChange={setLogo} hint={c.form.fileHint} optionalLabel={c.form.optional} />
        <FilePicker label={L.guidelines} id="hf-guidelines" file={guidelines} onChange={setGuidelines} hint={c.form.fileHint} optionalLabel={c.form.optional} />
      </div>

      <label htmlFor="hf-message" className="mt-8 block">
        <span className="mb-2 block font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">{L.message}</span>
        <textarea id="hf-message" data-testid="hf-message" rows={4} value={form.message} onChange={set("message")}
          className={`${inputCls} resize-y`} placeholder={c.form.messagePlaceholder} />
      </label>

      <label htmlFor="hf-privacy" className="mt-8 flex cursor-pointer items-start gap-3">
        <input id="hf-privacy" data-testid="hf-privacy" type="checkbox" required checked={privacy} onChange={(e) => setPrivacy(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer appearance-none border border-white/25 bg-black/40 transition-colors checked:border-swapa-yellow checked:bg-swapa-yellow" />
        <span className="text-xs leading-relaxed text-zinc-400">
          {c.form.privacyPre} <Link to="/privacy" className="text-swapa-yellow underline-offset-4 hover:underline">{c.form.privacyLink}</Link> {c.form.privacyPost}
        </span>
      </label>

      <button type="submit" disabled={sending} data-testid="hf-submit"
        className="group mt-10 inline-flex w-full items-center justify-center gap-3 bg-swapa-yellow px-8 py-5 font-mono text-xs font-semibold uppercase tracking-[0.3em] text-black transition-colors duration-300 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto">
        {sending ? c.form.sending : c.form.submit}
        {!sending && <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />}
      </button>
    </form>
  );
}

function DesignSection({ c, lang }) {
  return (
    <section id="design-your-swapa" data-testid="design-section" className="relative overflow-hidden border-t border-white/10 py-28 sm:py-40">
      <div className="pointer-events-none absolute inset-0 opacity-[0.07]" aria-hidden="true">
        <img src="/assets/monoq-exploded.webp" alt="" className="h-full w-full object-cover" loading="lazy" />
      </div>
      <div className="relative mx-auto max-w-[1600px] px-5 sm:px-10">
        <SectionTag index="05" label={c.form.tag} />
        <div className="mt-12 grid gap-14 lg:grid-cols-[1fr_1.2fr] lg:gap-20">
          <div>
            <h2 className="font-display font-extrabold uppercase leading-[0.95] tracking-tight">
              <Lines lines={c.form.lines} stagger={0.14} className="text-4xl text-white sm:text-5xl lg:text-6xl" />
            </h2>
            <Reveal delay={0.25}>
              <p className="mt-8 max-w-md font-serif text-lg italic leading-relaxed text-zinc-400">{c.form.copy}</p>
              <ul className="mt-10 space-y-4">
                {c.form.bullets.map((t) => (
                  <li key={t} className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300">
                    <Check className="h-3.5 w-3.5 text-swapa-yellow" />{t}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal delay={0.15} y={60}>
            <LeadForm c={c} lang={lang} />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function FinalCta({ c }) {
  return (
    <section className="relative overflow-hidden border-t border-white/10 py-32 sm:py-48">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <img src="/assets/oneplus-cockpit.webp" alt="" className="h-full w-full object-cover opacity-25" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/60 to-ink" />
      </div>
      <div className="relative mx-auto max-w-[1600px] px-5 text-center sm:px-10">
        <h2 className="font-display font-extrabold uppercase leading-[0.92] tracking-tight">
          <Lines lines={c.final.lines} stagger={0.14} className="text-4xl text-white sm:text-6xl lg:text-7xl" />
        </h2>
        <Reveal delay={0.3}>
          <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.3em] text-swapa-yellow">{c.final.tagline}</p>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <button onClick={scrollToForm} data-testid="final-cta-design"
              className="group inline-flex items-center gap-3 bg-swapa-yellow px-8 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-white">
              {c.final.cta1}
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </button>
            <a href="mailto:info@swapa.it?subject=SWAPA%20Hospitality" data-testid="final-cta-contact"
              className="group inline-flex items-center gap-3 border border-white/20 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:border-swapa-yellow hover:text-swapa-yellow">
              {c.final.cta2}
            </a>
          </div>
          <p className="mt-10 text-sm text-zinc-500">
            {c.final.testdrivePre}{" "}
            <Link to="/test-drive" className="text-swapa-yellow underline-offset-4 hover:underline" data-testid="final-cta-testdrive">
              {c.final.testdriveLink}
            </Link>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function StickyCta({ c }) {
  return (
    <div data-testid="hospitality-sticky-cta"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-black/85 backdrop-blur-xl sm:hidden">
      <button onClick={scrollToForm} data-testid="sticky-cta-button"
        className="flex w-full items-center justify-center gap-3 bg-swapa-yellow px-6 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-black">
        {c.sticky}
        <ArrowUpRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function Hospitality({ lang }) {
  const c = COPY[lang];
  useEffect(() => {
    document.title = c.seo.title;
    document.documentElement.lang = lang;
    let meta = document.querySelector('meta[name="description"]');
    const prev = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", c.seo.desc);
    const added = [];
    const alt = { en: "/hospitality", it: "/hospitality/it" };
    Object.entries(alt).forEach(([hl, path]) => {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = hl;
      link.href = `https://swapa.it${path}`;
      link.className = "hospitality-hreflang";
      document.head.appendChild(link);
      added.push(link);
    });
    const xd = document.createElement("link");
    xd.rel = "alternate";
    xd.hreflang = "x-default";
    xd.href = "https://swapa.it/hospitality";
    xd.className = "hospitality-hreflang";
    document.head.appendChild(xd);
    added.push(xd);
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "hospitality-breadcrumb-ld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "SWAPA", item: "https://swapa.it/" },
        { "@type": "ListItem", position: 2, name: "SWAPA Hospitality", item: `https://swapa.it${c.seo.path}` },
      ],
    });
    document.head.appendChild(ld);
    return () => {
      if (meta && prev != null) meta.setAttribute("content", prev);
      document.documentElement.lang = "it";
      added.forEach((l) => l.remove());
      document.getElementById("hospitality-breadcrumb-ld")?.remove();
    };
  }, [c, lang]);

  return (
    <div data-testid="hospitality-page">
      <Hero c={c} />
      <Intro c={c} />
      <Pillars c={c} />
      <Customization c={c} />
      <Comparison c={c} />
      <DesignSection c={c} lang={lang} />
      <FinalCta c={c} />
      <StickyCta c={c} />
    </div>
  );
}

export default function HospitalityPage() {
  return <Hospitality lang="en" />;
}

export function HospitalityPageIt() {
  return <Hospitality lang="it" />;
}
